import { Landmark, Sigma } from 'lucide-react';
import { PageBreadcrumb } from '@/components/ui/PageBreadcrumb';
import type { CarryEntity } from '@/types/carryFund';
import { formatUnits } from '../funds/carryFundValues';
import { CarryStatStrip, type CarryStat } from '../funds/CarryStatStrip';
import { CarryEntityHeader } from './CarryEntityHeader';
import { CarryEntityHoldersTable } from './CarryEntityHoldersTable';
import { CARRY_STRUCTURE_PATH } from './carryStructurePaths';

interface CarryEntityDetailProps {
  entity: CarryEntity;
  structurePath?: string;
  kind: string;
  kindClassName?: string;
  description: string;
  holdersLabel: string;
  holdersTitle: string;
  holdersNote: string;
}

function unitTotals({ funds, total_units }: CarryEntity): CarryStat[] {
  return [
    ...funds
      .filter((fund) => fund.units > 0)
      .map((fund) => ({
        icon: Landmark,
        label: fund.name,
        value: formatUnits(fund.units),
      })),
    { icon: Sigma, label: 'Total', value: formatUnits(total_units) },
  ];
}

export function CarryEntityDetail({
  entity,
  structurePath = CARRY_STRUCTURE_PATH,
  kind,
  kindClassName,
  description,
  holdersLabel,
  holdersTitle,
  holdersNote,
}: CarryEntityDetailProps) {
  const title = entity.name ?? kind;

  return (
    <div className="space-y-6">
      <PageBreadcrumb
        segments={[
          { label: 'Structure', href: structurePath },
          { label: title },
        ]}
      />

      <CarryEntityHeader
        kind={kind}
        kindClassName={kindClassName}
        title={title}
        description={description}
        facts={[
          { label: 'CoC', value: entity.coc_number ?? '—' },
          { label: 'Board', value: entity.board },
          { label: 'Holders', value: holdersLabel },
          { label: 'Units', value: formatUnits(entity.total_units) },
        ]}
      />

      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">{holdersTitle}</h3>
          <p className="text-sm text-muted-foreground">{holdersNote}</p>
        </div>
        {entity.holders.length === 0 ? (
          <p className="text-sm text-muted-foreground">No positions.</p>
        ) : (
          <>
            <CarryStatStrip stats={unitTotals(entity)} />
            <div className="pt-4">
              <CarryEntityHoldersTable
                funds={entity.funds}
                holders={entity.holders}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
